/**
 * Marketplace Header — compact, Alibaba-style.
 * Contains ONLY: promo strip + logo + utilities + secondary nav.
 * Search tabs and search bar live in MarketplaceHeroSearch (page body).
 */
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import NotificationBell from '../NotificationBell/NotificationBell';
import './MarketplaceHomeHeader.css';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'us' },
  { code: 'ar', name: 'العربية', flag: 'sa' },
];

export default function MarketplaceHomeHeader() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { selectedCurrency, currencies, changeCurrency } = useCurrency();
  const { user, isAuthenticated, isAdmin, isSupplier, logout } = useAuth();
  const { specialist, isAuthenticated: isSpecialistAuthenticated, logout: specialistLogout } = useSpecialistAuth();
  const isLoggedIn       = isAuthenticated || isSpecialistAuthenticated;
  const isSpecialist     = isSpecialistAuthenticated && !isAuthenticated;

  const [showLangDD,    setShowLangDD]    = useState(false);
  const [showUserMenu,  setShowUserMenu]  = useState(false);
  const [selectedLang,  setSelectedLang]  = useState({
    code: i18n.language || 'en',
    name: i18n.language === 'ar' ? 'العربية' : 'English',
    flag: i18n.language === 'ar' ? 'sa' : 'us',
  });

  const langRef       = useRef(null);
  const userMenuRef   = useRef(null);
  const profileBtnRef = useRef(null);

  /* ── outside-click ── */
  useEffect(() => {
    const handler = (e) => {
      if (langRef.current     && !langRef.current.contains(e.target))     setShowLangDD(false);
      const inUser    = userMenuRef.current?.contains(e.target);
      const inProfile = profileBtnRef.current?.contains(e.target);
      if (!inUser && !inProfile && !e.target.closest?.('.mhh-user-menu')) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── language ── */
  const selectLang = (lang) => {
    setSelectedLang(lang);
    setShowLangDD(false);
    i18n.changeLanguage(lang.code);
    localStorage.setItem('language', lang.code);
    document.documentElement.dir  = lang.code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang.code;
  };

  /* ── logout ── */
  const handleLogout = () => {
    if (isSpecialistAuthenticated) specialistLogout();
    if (isAuthenticated) logout();
    ['token', 'user', 'specialist_token', 'specialist'].forEach(k => localStorage.removeItem(k));
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <header className="marketplace-site-header mhh-root">

      {/* ══ 1. PROMO STRIP ══ */}
      <div className="mhh-promo-strip">
        <div className="mhh-container">
          <span className="mhh-promo-text">
            <strong>Conflux B2B</strong>&nbsp;|&nbsp;Design, source, sell — procurement led by smart sourcing tools.
          </span>
          <div className="mhh-promo-right">
            <span className="mhh-avatar-stack" aria-hidden>
              {[0,1,2,3].map(i => <span key={i} className="mhh-avatar" />)}
            </span>
            <button
              type="button"
              className="mhh-free-trial-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('marketplace-open-register'))}
            >
              Free trial →
            </button>
          </div>
        </div>
      </div>

      {/* ══ 2. MAIN HEADER ROW: Logo | Utilities ══ */}
      <div className="mhh-main-bar">
        <div className="mhh-container mhh-main-row">

          {/* Logo */}
          <Link to="/" className="mhh-logo">
            <img src="/logo.png?v=2" alt="Conflux" className="mhh-logo-img" />
            <span className="mhh-logo-text">B2B</span>
          </Link>

          {/* ── Utilities ── */}
          <div className="mhh-utils">

            {/* Deliver to */}
            <div className="mhh-deliver">
              <img src="https://flagcdn.com/w40/ae.png" alt="UAE flag" className="mhh-flag" />
              <div className="mhh-deliver-text">
                <span className="mhh-deliver-label">Deliver to</span>
                <span className="mhh-deliver-val">UAE</span>
              </div>
            </div>

            <div className="mhh-sep" />

            {/* Language + Currency dropdown */}
            <div className="mhh-rel" ref={langRef}>
              <button type="button" className="mhh-lang-btn" onClick={() => setShowLangDD(v => !v)}>
                {/* Globe icon */}
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="mhh-globe">
                  <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                <span className="mhh-lang-label">{selectedLang.name}-{selectedCurrency.code}</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="#ff6a00" className="mhh-caret"><path d="M18 9.5L12 15.5L6 9.5H18Z"/></svg>
              </button>
              {showLangDD && (
                <div className="mhh-dd-menu">
                  <p className="mhh-dd-group">Language</p>
                  {LANGUAGES.map(lang => (
                    <button key={lang.code} type="button"
                      className={`mhh-dd-item ${selectedLang.code === lang.code ? 'mhh-dd-item--on' : ''}`}
                      onClick={() => selectLang(lang)}
                    >
                      <img src={`https://flagcdn.com/w40/${lang.flag}.png`} alt="" className="mhh-flag-sm" />
                      {lang.name}
                    </button>
                  ))}
                  <p className="mhh-dd-group" style={{ marginTop: 6 }}>Currency</p>
                  {currencies.map(curr => (
                    <button key={curr.code} type="button"
                      className={`mhh-dd-item ${selectedCurrency.code === curr.code ? 'mhh-dd-item--on' : ''}`}
                      onClick={() => { changeCurrency(curr.code); setShowLangDD(false); }}
                    >
                      {curr.code} · {curr.symbol}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mhh-sep" />

            {/* Cart */}
            <NavLink to="/compare" className="mhh-icon-btn" title="Cart">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
            </NavLink>

            {/* Wishlist / messages / notif / profile — visible md+ */}
            <div className="mhh-icon-bar">
              <NavLink to="/favorites" className="mhh-icon-btn" title="Wishlist">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                </svg>
              </NavLink>
              <NavLink to="/messages" className="mhh-icon-btn" title="Messages">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
              </NavLink>
              <div className="mhh-icon-btn" style={{ padding: 2 }}>
                <NotificationBell />
              </div>
            </div>

            <div className="mhh-sep" />

            {/* Auth */}
            {isLoggedIn ? (
              <div className="mhh-rel" ref={userMenuRef}>
                <button type="button" className="mhh-user-btn" ref={profileBtnRef} onClick={() => setShowUserMenu(v => !v)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span className="mhh-user-name">{user?.name || specialist?.name || 'Account'}</span>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="#ff6a00"><path d="M18 9.5L12 15.5L6 9.5H18Z"/></svg>
                </button>
                {showUserMenu && ReactDOM.createPortal(
                  <>
                    <div className="mhh-overlay" onClick={() => setShowUserMenu(false)} />
                    <div
                      className="mhh-user-menu mhh-dd-menu"
                      style={{
                        position: 'fixed', zIndex: 399,
                        top:  (userMenuRef.current?.getBoundingClientRect().bottom ?? 0) + 6,
                        left: (userMenuRef.current?.getBoundingClientRect().left   ?? 0),
                      }}
                    >
                      <button type="button" className="mhh-dd-item"
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate(isSpecialist ? '/specialist-dashboard' : isSupplier ? '/vendor-dashboard' : isAdmin ? '/admin/dashboard' : '/profile');
                        }}
                      >Dashboard</button>
                      <button type="button" className="mhh-dd-item mhh-dd-item--danger" onClick={handleLogout}>Logout</button>
                    </div>
                  </>,
                  document.body
                )}
              </div>
            ) : (
              <div className="mhh-auth-row">
                <button
                  type="button"
                  className="mhh-signin-btn"
                  onClick={() => window.dispatchEvent(new CustomEvent('marketplace-open-login'))}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  Sign in
                </button>
                <button
                  type="button"
                  className="mhh-create-btn"
                  onClick={() => window.dispatchEvent(new CustomEvent('marketplace-open-register'))}
                >
                  Create account
                </button>
              </div>
            )}
          </div>
          {/* end .mhh-utils */}

        </div>
      </div>

      {/* ══ 3. SECONDARY NAV ══ */}
      <div className="mhh-sec-nav">
        <div className="mhh-container mhh-sec-inner">
          <nav className="mhh-sec-left">
            <NavLink to="/all-categories" className="mhh-sec-link mhh-sec-link--bold">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ color: '#ff6a00', flexShrink: 0 }}>
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
              All categories
            </NavLink>
            <NavLink to="/top-seller"       className="mhh-sec-link">Verified manufacturers</NavLink>
            <NavLink to="/terms-conditions" className="mhh-sec-link">Order protections</NavLink>
          </nav>
          <nav className="mhh-sec-right">
            <NavLink to="/membership-packages" className="mhh-sec-link mhh-sec-sm">Tax exemption</NavLink>
            <NavLink to="/submit-rfq"          className="mhh-sec-link mhh-sec-sm">Buyer Central</NavLink>
            <NavLink to="/compare"             className="mhh-sec-link mhh-sec-sm">App &amp; extension</NavLink>
            <NavLink to="/documents-submit"    className="mhh-sec-link mhh-sec-sm">Sell on platform</NavLink>
            <NavLink to="/news"                className="mhh-sec-link mhh-sec-sm mhh-xl-only">Industry news</NavLink>
            <NavLink to="/faq"                 className="mhh-sec-link mhh-sec-sm">Help centre</NavLink>
          </nav>
        </div>
      </div>

    </header>
  );
}
